import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatMoney } from '../data/currencies';

export type VoiceOrderState = {
  status: 'pending' | 'delivered' | 'declined' | 'expired';
  charged?: number | string | null;
  expires_at?: string | null;
};

export type VoiceOrderCard = {
  orderId: number;
  seconds: number;
  amount: number;
  currency: string;
};

/** Kart içeriği sunucuda JSON olarak yazılıyor; bozuksa satır çizilmez. */
export function parseVoiceOrderCard(content: string): VoiceOrderCard | null {
  try {
    const d = JSON.parse(content);
    if (typeof d?.seconds !== 'number' || typeof d?.amount !== 'number') return null;
    if (typeof d?.currency !== 'string') return null;
    return { orderId: Number(d.orderId), seconds: d.seconds, amount: d.amount, currency: d.currency };
  } catch {
    return null;
  }
}

export function formatOrderLength(seconds: number): string {
  const tam = Math.max(0, Math.round(seconds));
  return `${Math.floor(tam / 60)}:${String(tam % 60).padStart(2, '0')}`;
}

const DURUM_ANAHTARI = {
  pending: 'message.voiceOrderPending',
  delivered: 'message.voiceOrderDelivered',
  declined: 'message.voiceOrderDeclined',
  expired: 'message.voiceOrderExpired',
} as const;

type Props = {
  card: VoiceOrderCard;
  state: VoiceOrderState | null;
  mine: boolean;
  language: string;
  onLongPress: () => void;
  /** 9.1.10 — yalnız satıcıda ve yalnız bekleyen siparişte çağrılır. */
  onRecord?: () => void;
  onDecline?: () => void;
  /** Bir istek sürüyorken iki düğme de kapanır: çift teslim/çift ret olmasın. */
  busy?: boolean;
};

/**
 * Sohbetteki sipariş satırı (9.1.8 · D-573).
 *
 * Dört sorudan üçüncüsünün cevabı: sipariş ayrı bir ekranda değil, konuşmanın
 * içinde duruyor — buluşma teklifi gibi. Teslim de aynı yere düşüyor, yani
 * ısmarlayan tek bir yere bakıyor.
 *
 * Kartın SAYILARI açılışta donuyor (ne kadar süre, ne kadar para), DURUMU ise
 * sipariş satırından okunuyor. Durum da donsaydı teslim edilmiş bir sipariş
 * sohbette hâlâ "bekliyor" görünürdü.
 *
 * 9.1.10 — aynı satır iki tarafta AYNI DEĞİL. Satıcı bekleyen bir siparişte iki
 * düğme görüyor; ısmarlayan sadece durumu. Bugüne kadar iki taraf da aynı
 * hareketsiz kartı görüyordu: satıcı siparişi görüyor ama yapabileceği hiçbir
 * şey yoktu, yani sunucudaki teslim ve ret uçlarına hiçbir yerden ulaşılamıyordu.
 */
export default function MessageVoiceOrder({
  card, state, mine, language, onLongPress, onRecord, onDecline, busy,
}: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const durum = state?.status ?? 'pending';
  const renk = mine ? colors.white : colors.black;
  // Teslim edilen siparişte gerçekten kesilen tutar gösteriliyor: kısa teslimde
  // ısmarlanan ile ödenen farklıdır ve ödenen doğru sayıdır (D-573).
  const kesilen = durum === 'delivered' && state?.charged != null
    ? Number(state.charged) : null;
  // Kısa teslimin sayısı kendi başına açıklanmıyor: ısmarlayan neden daha az
  // ödediğini anlamalı, yoksa kart yalnızca beklenenden küçük bir rakam gösterir.
  const iade = kesilen != null && kesilen < card.amount
    ? parseFloat((card.amount - kesilen).toFixed(2)) : null;
  const satici = !mine && durum === 'pending' && (onRecord != null || onDecline != null);

  return (
    <TouchableOpacity testID="message-voice-order" activeOpacity={0.9} onLongPress={onLongPress}>
      <View style={styles.card}>
        <Text style={[styles.title, { color: renk }]}>{t('message.voiceOrderTitle')}</Text>
        <Text testID="voice-order-detail" style={[styles.detail, { color: renk }]}>
          {formatOrderLength(card.seconds)} · {formatMoney(kesilen ?? card.amount, card.currency, language)}
        </Text>
        <Text testID="voice-order-status" style={[styles.status, { color: renk }]}>
          {t(DURUM_ANAHTARI[durum] ?? DURUM_ANAHTARI.pending)}
        </Text>
        {iade != null && (
          <Text testID="voice-order-refund" style={[styles.status, { color: renk }]}>
            {t('message.voiceOrderRefunded', { amount: formatMoney(iade, card.currency, language) })}
          </Text>
        )}
        {satici && (
          <View style={styles.actions}>
            <TouchableOpacity
              testID="voice-order-record"
              disabled={busy}
              onPress={onRecord}
              style={[styles.action, { borderColor: renk, opacity: busy ? 0.5 : 1 }]}>
              <Text style={[styles.actionText, { color: renk }]}>{t('message.voiceOrderRecord')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="voice-order-decline"
              disabled={busy}
              onPress={onDecline}
              style={[styles.action, { borderColor: renk, opacity: busy ? 0.5 : 1 }]}>
              <Text style={[styles.actionText, { color: renk }]}>{t('message.voiceOrderDecline')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: { minWidth: 180, gap: spacing.xs, borderRadius: radius.card },
  title: { fontSize: fontSize.md, fontWeight: '600' },
  detail: { fontSize: fontSize.sm, opacity: 0.9 },
  status: { fontSize: fontSize.sm, opacity: 0.7 },
  actions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  // Dokunma alanı kasıtlı olarak geniş: canlı paylaşımda küçük bir durdurma
  // düğmesi iki dokunuşta hiç istek üretmemişti (D-586).
  action: {
    flex: 1, minHeight: 44, borderWidth: 1, borderRadius: radius.control,
    alignItems: 'center', justifyContent: 'center', paddingHorizontal: spacing.sm,
  },
  actionText: { fontSize: fontSize.sm, fontWeight: '600' },
});
